// ============================================================================
// CaseSecuritiesPanel — "Security Details" block on Summary
// ============================================================================
// One row per linked security. Each row shows:
//   • Asset description (vehicle reg + make/model for VEHICLE; security_code +
//     description otherwise)
//   • In-scope toggle (edit gated by litigation-case.edit_meta)
//   • [Primary] badge / "Set primary" link
//   • Insurance + valuation chips when relevant
//
// Used inside SummarySection (Overview > Summary) and surfaced as a static
// read-only "Targets" strip in the REPO_ASSIGNED form panel (Phase 5.6d).

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import {
  useCaseSecurities,
  useUpdateCaseSecurityScope,
  useSetPrimaryCaseSecurity,
} from 'hooks/api/useCaseSecurities';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import type { CaseSecurity } from 'api/types/caseSecurity.types';

export interface CaseSecuritiesPanelProps {
  caseId: string;
  /** When true, only renders in-scope rows; no toggle controls; no headers
   *  beyond a short label. For use as the REPO_ASSIGNED "Targets" strip. */
  compact?: boolean;
}

const CaseSecuritiesPanel: React.FC<CaseSecuritiesPanelProps> = ({ caseId, compact }) => {
  const permissions = useRecsuitPermissions();
  const { data: securities, loading, refetch } = useCaseSecurities(caseId);
  const { updateScope } = useUpdateCaseSecurityScope();
  const { setPrimary } = useSetPrimaryCaseSecurity();

  const canEdit = permissions.hasAction('litigation-case', 'edit_meta');
  const rows = compact ? securities.filter((s) => s.inScope) : securities;

  const handleToggle = useCallback(async (sec: CaseSecurity) => {
    if (!canEdit) return;
    try {
      await updateScope(sec.id, {
        inScope: !sec.inScope,
        scopeNotes: sec.scopeNotes ?? null,
        rowVersion: sec.rowVersion,
      });
      await refetch();
      notificationService.toast.success(
        `Security ${sec.securityCode} marked ${!sec.inScope ? 'in scope' : 'out of scope'}.`,
      );
    } catch (e) {
      notificationService.toast.error(
        e instanceof Error ? e.message : 'Failed to update scope.',
      );
    }
  }, [canEdit, updateScope, refetch]);

  const handleSetPrimary = useCallback(async (sec: CaseSecurity) => {
    if (!canEdit || sec.isPrimary) return;
    try {
      await setPrimary(sec.id, { rowVersion: sec.rowVersion });
      await refetch();
      notificationService.toast.success(`Set ${sec.securityCode} as primary.`);
    } catch (e) {
      notificationService.toast.error(
        e instanceof Error ? e.message : 'Failed to set primary.',
      );
    }
  }, [canEdit, setPrimary, refetch]);

  if (loading && rows.length === 0) {
    return <div style={mutedStyle}>Loading securities…</div>;
  }
  if (rows.length === 0) {
    return (
      <div style={mutedStyle}>
        {compact ? 'No targets in scope.' : 'No linked securities. They will appear when the case is claimed.'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '0.35rem' : '0.5rem' }}>
      {rows.map((sec) => (
        <SecurityRow
          key={sec.id}
          sec={sec}
          compact={!!compact}
          canEdit={canEdit && !compact}
          onToggle={() => handleToggle(sec)}
          onSetPrimary={() => handleSetPrimary(sec)}
        />
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------

const SecurityRow: React.FC<{
  sec: CaseSecurity;
  compact: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onSetPrimary: () => void;
}> = ({ sec, compact, canEdit, onToggle, onSetPrimary }) => {
  const title = formatSecurityTitle(sec);
  const meta = formatSecurityMeta(sec);
  const inScope = sec.inScope;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
      padding: compact ? '0.4rem 0.6rem' : '0.6rem 0.85rem',
      border: '1px solid var(--border-color)',
      borderLeft: sec.isPrimary
        ? '4px solid #2563eb'
        : inScope ? '4px solid #16a34a' : '4px solid var(--text-secondary)',
      borderRadius: 4,
      background: inScope ? 'var(--bg-primary)' : 'var(--bg-secondary)',
      opacity: inScope ? 1 : 0.7,
    }}>
      {/* Toggle / checkbox — small styled box; icon-collection lacks
          checkSquare/square, so we render a div with the check icon inside. */}
      {!compact && (
        <button
          type="button"
          onClick={onToggle}
          disabled={!canEdit}
          title={canEdit
            ? (inScope ? 'Mark out of scope' : 'Mark in scope')
            : 'You don\'t have permission to edit scope'}
          style={{
            marginTop: 2, padding: 0,
            border: 'none', background: 'transparent',
            cursor: canEdit ? 'pointer' : 'not-allowed',
            display: 'inline-flex',
          }}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 18, height: 18, borderRadius: 3,
            border: `1.5px solid ${inScope ? '#16a34a' : 'var(--text-secondary)'}`,
            background: inScope ? '#16a34a' : 'transparent',
          }}>
            {inScope && <IconSystem.check size={12} color="white" />}
          </span>
        </button>
      )}
      {compact && (
        <IconSystem.check size={14} style={{ color: '#16a34a', marginTop: 2 }} />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: compact ? '0.82rem' : '0.88rem' }}>{title}</strong>
          {sec.isPrimary && (
            <span style={primaryBadge}>Primary</span>
          )}
          {!inScope && !compact && (
            <span style={outOfScopeBadge}>Out of scope</span>
          )}
        </div>
        {meta && (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
            {meta}
          </div>
        )}
        {sec.scopeNotes && !compact && (
          <div style={{
            marginTop: '0.3rem', fontSize: '0.74rem',
            fontStyle: 'italic', color: 'var(--text-secondary)',
          }}>{sec.scopeNotes}</div>
        )}
        {canEdit && !sec.isPrimary && inScope && (
          <button
            type="button"
            onClick={onSetPrimary}
            style={{
              marginTop: '0.35rem', padding: '1px 8px',
              fontSize: '0.7rem', fontWeight: 600,
              background: 'transparent', color: '#2563eb',
              border: '1px solid #2563eb', borderRadius: 4,
              cursor: 'pointer',
            }}
          >Set as Primary</button>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------

function formatSecurityTitle(sec: CaseSecurity): string {
  if (sec.securityType === 'VEHICLE') {
    const reg  = sec.vehicleRegistrationNo ?? '';
    const make = [sec.vehicleYear, sec.vehicleMake, sec.vehicleModel].filter(Boolean).join(' ');
    return reg && make ? `${reg} · ${make}` : reg || make || sec.securityCode;
  }
  return sec.description?.trim() || sec.securityCode;
}

function formatSecurityMeta(sec: CaseSecurity): string {
  const parts: string[] = [sec.securityType];
  if (sec.chargeType) parts.push(sec.chargeType);
  if (sec.securityValue != null) {
    parts.push(`Valuation ₹${sec.securityValue.toLocaleString('en-IN')}`);
  }
  if (sec.securityType === 'VEHICLE') {
    if (sec.vehicleCategory) parts.push(sec.vehicleCategory);
    if (sec.vehicleColor) parts.push(sec.vehicleColor);
    if (sec.insurancePolicyNo) parts.push(`Insured ${sec.insurancePolicyNo}`);
    if (sec.insuranceExpiryDate) {
      parts.push(`till ${new Date(sec.insuranceExpiryDate).toLocaleDateString('en-IN')}`);
    }
  }
  return parts.join(' · ');
}

const mutedStyle: React.CSSProperties = {
  padding: '0.65rem 0.85rem',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const primaryBadge: React.CSSProperties = {
  fontSize: '0.65rem', fontWeight: 600,
  padding: '1px 8px', borderRadius: '999px',
  background: '#dbeafe', color: '#1e40af',
};

const outOfScopeBadge: React.CSSProperties = {
  fontSize: '0.65rem', fontWeight: 600,
  padding: '1px 8px', borderRadius: '999px',
  background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
};

export default CaseSecuritiesPanel;
